import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSavedCards as clientGetSavedCards,
  deleteSavedCard as clientDeleteSavedCard,
} from 'typescript/clients/solidshop/payment_sources_client';
import { QUERY_KEYS, MUTATION_KEYS } from 'typescript/constants/react_query';

const useGetSavedCards = ({
  email,
  merchantToken,
  enabled = true,
}: {
  email: string;
  merchantToken: string;
  enabled?: boolean;
}) => {
  const { data: savedCardsData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.PAYMENT_SOURCES, { merchantToken, email }],
    queryFn: () => clientGetSavedCards(),
    enabled,
  });

  return { savedCardsData, ...rest };
};

const useDeleteSavedCard = ({
  email,
  merchantToken,
}: {
  email: string;
  merchantToken: string;
}) => {
  const queryClient = useQueryClient();

  const { mutate: deleteSavedCard, ...rest } = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_SAVED_CARD],
    mutationFn: ({ cardId }: { cardId: number }) =>
      clientDeleteSavedCard({
        cardId,
      }),
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYMENT_SOURCES, { merchantToken, email }],
      });
    },
    onError: (error) => {
      captureException(
        new CustomError(MUTATION_KEYS.DELETE_SAVED_CARD, error),
        {
          extra: {
            merchantToken,
          },
        },
      );
    },
  });

  return { deleteSavedCard, ...rest };
};

export { useGetSavedCards, useDeleteSavedCard };
